import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { notesFraisAPI } from '../services/api';
import { useAuth } from '../context/AuthContext';
import toast from 'react-hot-toast';
import {
  Plus, RefreshCw, ReceiptText, Eye, Trash2, Send, Pencil, XCircle,
} from 'lucide-react';
import './NotesFrais.css';

const POSTE_LABELS = {
  FRAIS_DIVERS:       'Frais divers',
  MISSION:            'Mission',
  RECEPTION:          'Réception',
  VOYAGE_DEPLACEMENT: 'Voyage & déplacement',
};

const STATUT_META = {
  BROUILLON: { label: 'Brouillon', cls: 'nf-badge--brouillon' },
  SOUMIS:    { label: 'Soumis',    cls: 'nf-badge--soumis' },
  VALIDE:    { label: 'Validé',    cls: 'nf-badge--valide' },
  REFUSE:    { label: 'Refusé',    cls: 'nf-badge--refuse' },
  ANNULE:    { label: 'Annulé',    cls: 'nf-badge--annule' },
};

function fmtDate(iso) {
  if (!iso) return '—';
  const [y, m, d] = iso.split('-');
  return `${d}/${m}/${y}`;
}
function fmtMontant(val) {
  return new Intl.NumberFormat('fr-FR', { style: 'currency', currency: 'EUR' }).format(val);
}

export default function NotesFrais() {
  const { user } = useAuth();
  const navigate = useNavigate();

  const [notes,   setNotes]   = useState([]);
  const [stats,   setStats]   = useState({ nb_soumis: 0, montant_soumis: 0, montant_en_cours: 0 });
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [nRes, sRes] = await Promise.all([
        notesFraisAPI.list(),
        notesFraisAPI.stats(),
      ]);
      setNotes(nRes.data || []);
      setStats(sRes.data || { nb_soumis: 0, montant_soumis: 0, montant_en_cours: 0 });
    } catch {
      toast.error('Erreur chargement notes de frais');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const handleCancel = async (id) => {
    if (!window.confirm('Annuler cette note de frais ?')) return;
    try {
      await notesFraisAPI.cancel(id);
      toast.success('Note annulée');
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Erreur annulation');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Supprimer cette note de frais ?')) return;
    try {
      await notesFraisAPI.remove(id);
      toast.success('Note supprimée');
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Erreur suppression');
    }
  };

  const handleSubmit = async (id) => {
    try {
      await notesFraisAPI.submit(id);
      toast.success('Note soumise pour validation');
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Erreur soumission');
    }
  };

  return (
    <div className="nf-landing">
      {/* Header */}
      <div className="page-header">
        <div className="page-header-left">
          <h1>Notes de frais</h1>
          <p>Gérez vos justificatifs de dépenses professionnelles</p>
        </div>
        <div className="page-header-actions">
          <button className="btn btn-secondary" onClick={load}><RefreshCw size={15}/></button>
          <button className="btn btn-primary" onClick={() => navigate('/notes-frais/nouveau')}>
            <Plus size={15}/> Déposer un frais
          </button>
        </div>
      </div>

      {/* Stats */}
      <div className="nf-stats">
        <div className="nf-stat-card">
          <span className="nf-stat-label">Frais soumis</span>
          <span className="nf-stat-value accent">{stats.nb_soumis}</span>
        </div>
        <div className="nf-stat-card">
          <span className="nf-stat-label">Montant soumis</span>
          <span className="nf-stat-value">{fmtMontant(stats.montant_soumis)}</span>
        </div>
        <div className="nf-stat-card">
          <span className="nf-stat-label">Montant en cours</span>
          <span className="nf-stat-value">{fmtMontant(stats.montant_en_cours)}</span>
        </div>
      </div>

      {/* CTA */}
      <div className="nf-cta">
        <button className="btn btn-primary" onClick={() => navigate('/notes-frais/nouveau')}>
          <Plus size={15}/> Déposer un nouveau frais
        </button>
      </div>

      {/* List */}
      <div className="card">
        <div className="nf-list-header">
          <h3>Mes frais</h3>
          <span style={{ fontSize: '0.8125rem', color: 'var(--text-muted)' }}>
            {notes.length} note{notes.length !== 1 ? 's' : ''}
          </span>
        </div>

        {loading ? (
          <div className="loading-state"><div className="spinner"/></div>
        ) : notes.length === 0 ? (
          <div style={{ textAlign: 'center', padding: '40px 0', color: 'var(--text-muted)' }}>
            <ReceiptText size={36} style={{ opacity: 0.25, marginBottom: 12 }}/>
            <div style={{ fontWeight: 600, marginBottom: 6 }}>Aucune note de frais</div>
            <div style={{ fontSize: '0.875rem', marginBottom: 20 }}>Déposez votre premier justificatif</div>
            <button className="btn btn-primary" onClick={() => navigate('/notes-frais/nouveau')}>
              <Plus size={15}/> Déposer un frais
            </button>
          </div>
        ) : (
          <div className="table-wrap">
            <table>
              <thead>
                <tr>
                  <th>N°</th>
                  <th>Désignation</th>
                  <th>Poste</th>
                  <th>Date</th>
                  <th>Montant TTC</th>
                  <th>Projet</th>
                  <th>Statut</th>
                  <th/>
                </tr>
              </thead>
              <tbody>
                {notes.map(n => {
                  const sm = STATUT_META[n.statut] || STATUT_META.BROUILLON;
                  return (
                    <tr key={n.id}>
                      <td style={{ color: 'var(--text-muted)', fontSize: '0.8rem' }}>#{n.numero}</td>
                      <td style={{ fontWeight: 600, fontSize: '0.875rem' }}>{n.designation}</td>
                      <td style={{ fontSize: '0.8125rem' }}>{POSTE_LABELS[n.poste] || n.poste}</td>
                      <td style={{ fontSize: '0.8rem', color: 'var(--text-muted)' }}>{fmtDate(n.date_effet)}</td>
                      <td style={{ fontWeight: 600 }}>{fmtMontant(n.montant_ttc)}</td>
                      <td style={{ fontSize: '0.8125rem', color: 'var(--text-muted)' }}>{n.project_nom || '—'}</td>
                      <td>
                        <span className={`nf-badge ${sm.cls}`}>{sm.label}</span>
                      </td>
                      <td>
                        <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
                          <button
                            className="btn btn-ghost btn-sm"
                            title="Consulter"
                            onClick={() => navigate(`/notes-frais/${n.id}`)}
                          >
                            <Eye size={13}/>
                          </button>
                          {['BROUILLON', 'REFUSE'].includes(n.statut) && (
                            <button
                              className="btn btn-ghost btn-sm"
                              title="Modifier"
                              onClick={() => navigate(`/notes-frais/${n.id}`)}
                            >
                              <Pencil size={13}/>
                            </button>
                          )}
                          {n.statut === 'BROUILLON' && (
                            <button
                              className="btn btn-ghost btn-sm"
                              title="Soumettre"
                              style={{ color: '#3B82F6' }}
                              onClick={() => handleSubmit(n.id)}
                            >
                              <Send size={13}/>
                            </button>
                          )}
                          {['BROUILLON', 'SOUMIS'].includes(n.statut) && (
                            <button
                              className="btn btn-ghost btn-sm"
                              title="Annuler"
                              style={{ color: '#f59e0b' }}
                              onClick={() => handleCancel(n.id)}
                            >
                              <XCircle size={13}/>
                            </button>
                          )}
                          {['BROUILLON', 'REFUSE', 'ANNULE'].includes(n.statut) && (
                            <button
                              className="btn btn-ghost btn-sm"
                              title="Supprimer"
                              style={{ color: 'var(--danger-600)' }}
                              onClick={() => handleDelete(n.id)}
                            >
                              <Trash2 size={13}/>
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
